import React, { useEffect, useState } from "react";
import {
  Modal,
  TextInput,
  Button,
  Stack,
  Group,
  Select,
  Divider,
  Checkbox,
  Loader,
  Text,
  PasswordInput,
  Accordion,
  Card,
  Alert,
} from "@mantine/core";
import { UserRequest } from "types/requestes.type";
import { UserResponse } from "types/responses.type";
import { getPasswordProblems, isValidEmail } from "utils/string.helpers";
import ShipperService from "services/shipper.service";

interface UserPropertiesModalProps {
  user?: UserResponse;
  onSave?: (user: UserRequest) => void;
  onClose?: () => void;
  isSelfEdit?: boolean;
}

export default function UserPropertiesModal({
  user,
  isSelfEdit,
  onClose,
  onSave,
}: UserPropertiesModalProps) {
  const [editedUser, setEditedUser] = useState<Partial<UserRequest>>({});
  const [mode, setMode] = useState<"EDIT" | "CREATE">();
  const [saving, setSaving] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [changePassReq, setChangePassReq] = useState<{
    password?: string;
    newPassword: string;
    confirmPassword: string;
  }>();
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    newPassword?: string;
    confirmPassword?: string;
    api?: string;
  }>({});

  useEffect(() => {
    if (user) {
      setErrors({});
      setChangePassReq(undefined);
      if (user.userId) {
        setMode("EDIT");
        setShowPasswordFields(false);
        setEditedUser({
          userId: user.userId,
          email: user.email,
          displayName: user.displayName,
          type: user.type,
        });
      } else {
        setMode("CREATE");
        setShowPasswordFields(true);
        setChangePassReq({
          confirmPassword: "",
          newPassword: "",
          password: "",
        });
        setEditedUser({ type: user?.type });
      }
    }
  }, [user]);

  const handleChange = (field: keyof UserRequest, value: any) => {
    if (field === "email" && isValidEmail(value)) {
      setErrors((e) => ({ ...e, email: undefined }));
    }
    setEditedUser((u) => ({ ...u, [field]: value }));
  };

  const handleSave = async () => {
    setErrors({});
    const reqErrors: any = {};
    if (mode === "CREATE" && !isValidEmail(editedUser?.email))
      reqErrors.email = "Enter a valid email";
    if (showPasswordFields) {
      if (!changePassReq?.newPassword) {
        reqErrors.newPassword = "New password required";
      } else {
        const problems = getPasswordProblems(changePassReq.newPassword);
        if (problems) reqErrors.newPassword = problems;
      }
      if (!changePassReq?.confirmPassword) {
        reqErrors.confirmPassword = "Confirm password required";
      } else if (changePassReq.newPassword !== changePassReq.confirmPassword) {
        reqErrors.confirmPassword = "Passwords do not match";
      }
      if (isSelfEdit && !changePassReq?.password)
        reqErrors.password = "Password required";
    }
    if (Object.keys(reqErrors).length > 0) {
      setErrors(reqErrors);
      return;
    }
    setSaving(true);
    (mode === "EDIT"
      ? ShipperService.updateUser({ ...editedUser, ...(changePassReq ?? {}) })
      : ShipperService.createUser({ ...editedUser, ...(changePassReq ?? {}) })
    )
      .then((res) => {
        console.log(res);

        if (res.ok) {
          onSave && onSave(res.data);
          onClose && onClose();
        } else {
          setErrors((e) => ({ ...e, api: res.errorMessage }));
        }
      })
      .catch((err) => {
        setErrors((e) => ({ ...e, api: err.message }));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  function handlePassFieldsChange(field: string, value: string): void {
    setChangePassReq((r) => ({ ...r, [field]: value } as any));
  }

  return (
    <Modal
      opened={Boolean(user)}
      onClose={onClose ?? (() => {})}
      title={mode === "CREATE" ? "Create User" : "User Properties"}
      size="md"
    >
      <Stack gap="md">
        {mode === "CREATE" ? (
          <TextInput
            label="Email"
            value={editedUser.email ?? ""}
            onChange={(e) => handleChange("email", e.currentTarget.value)}
            disabled={saving}
            required
            autoComplete="username"
            error={errors.email}
            type="email"
          />
        ) : (
          <TextInput
            label="Email"
            value={editedUser.email}
            disabled
            autoComplete="username"
          />
        )}
        <Select
          label="Type"
          data={[
            { value: "SHIPPER_USER", label: "Shipper User" },
            { value: "SHIPPER_ADMIN", label: "Shipper Admin" },
            ...(!["SHIPPER_ADMIN", "SHIPPER_USER"].includes(
              editedUser.type ?? ""
            )
              ? [editedUser.type ?? ""]
              : []),
          ]}
          value={editedUser.type}
          onChange={(val) => handleChange("type", val)}
          w={"100%"}
          disabled={isSelfEdit}
        />
        <Divider />
        <TextInput
          label="Display Name"
          value={editedUser.displayName ?? ""}
          onChange={(e) => handleChange("displayName", e.currentTarget.value)}
          disabled={saving}
          autoComplete="off"
        />

        <Divider />
        {showPasswordFields && (
          <Card withBorder>
            <Stack gap="xs">
              {isSelfEdit && mode == "EDIT" && (
                <PasswordInput
                  label="Current Password"
                  placeholder="Your password"
                  required
                  value={changePassReq?.password}
                  onChange={(e) =>
                    handlePassFieldsChange("password", e.currentTarget.value)
                  }
                  disabled={saving}
                  mb={16}
                  autoComplete="new-password"
                  error={errors.password}
                />
              )}
              <PasswordInput
                name="new-password"
                label="New Password"
                value={changePassReq?.newPassword}
                onChange={(e) =>
                  handlePassFieldsChange("newPassword", e.currentTarget.value)
                }
                disabled={saving}
                placeholder="New password"
                required
                autoComplete="new-password"
                error={errors.newPassword}
              />
              <PasswordInput
                name="confirm-password"
                value={changePassReq?.confirmPassword}
                onChange={(e) =>
                  handlePassFieldsChange(
                    "confirmPassword",
                    e.currentTarget.value
                  )
                }
                disabled={saving}
                placeholder="Confirm new password"
                required
                autoComplete="new-password"
                error={errors.confirmPassword}
              />
            </Stack>
          </Card>
        )}
        {mode == "EDIT" && (
          <Button
            variant="light"
            size="xs"
            onClick={() => setShowPasswordFields((v) => !v)}
            mb={0}
          >
            {showPasswordFields ? "Cancel Password Change" : "Change Password"}
          </Button>
        )}

        {errors.api && (
          <Alert color="red" title="Error" mt="sm">
            {errors.api}
          </Alert>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={saving}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
